import { ApiHttpError, ApiUnauthorizedError } from './errors.js';

export interface AdminBffClientConfig {
  baseUrl: string;
}

let _config: AdminBffClientConfig = { baseUrl: '' };
let _accessToken: string | null = null;
let _refreshCallback: (() => Promise<string | null>) | null = null;
// Deduplicates concurrent 401 retries: all callers share one in-flight refresh.
let _refreshInFlight: Promise<string | null> | null = null;
// Fallback token getter — registered by AuthProvider so requests can pick up the
// latest token even if setAdminBffAccessToken hasn't been called yet (e.g. race
// between authLoader refresh and first React Query fire).
let _tokenGetter: (() => string | null) | null = null;

export function configureAdminBff(config: AdminBffClientConfig): void {
  _config = { ..._config, ...config };
}

export function getAdminBffBaseUrl(): string {
  return _config.baseUrl;
}

export function getAdminBffToken(): string | null {
  return _accessToken;
}

/**
 * Called by AuthProvider after a successful MSAL → BFF token exchange.
 * The token is included as `Authorization: Bearer` on every subsequent request.
 */
export function setAdminBffAccessToken(token: string | null): void {
  _accessToken = token;
}

/** Registered by AuthProvider to silently refresh the BFF JWT on 401. */
export function setRefreshCallback(cb: (() => Promise<string | null>) | null): void {
  _refreshCallback = cb;
}

/** Registered by bootstrap so requests can fall back to the live Zustand token. */
export function setTokenGetter(getter: (() => string | null) | null): void {
  _tokenGetter = getter;
}

function isVerbose(): boolean {
  try {
    return !!localStorage.getItem('arais:verbose');
  } catch {
    return false;
  }
}

function logRequest(method: string, url: string, status: number, ms: number): void {
  const tag = status >= 400 ? '🔴' : status >= 300 ? '🟡' : '🟢';
  console.debug(`${tag} [bff] ${method} ${url} → ${status} (${ms}ms)`);
}

/**
 * Custom orval fetch mutator for the Admin BFF.
 *
 * Attaches the BFF JWT issued after MSAL login as a Bearer token.
 * Returns { data, status, headers } matching orval's tagged-union response types.
 * Throws ApiUnauthorizedError on 401/403, ApiHttpError on other non-2xx.
 *
 * Verbose mode: set localStorage key `arais:verbose` to any value to enable
 * per-request timing logs. Toggle in DevTools console:
 *   localStorage.setItem('arais:verbose', '1')   // enable
 *   localStorage.removeItem('arais:verbose')       // disable
 */
export async function adminBffFetch<T>(url: string, options: RequestInit = {}): Promise<T> {
  const token = _accessToken ?? _tokenGetter?.() ?? null;
  const headers: Record<string, string> = {
    ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
    ...(options.headers as Record<string, string> | undefined),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  // Generated orval paths omit the /api/v1 prefix; compat stubs include it explicitly.
  const resolvedUrl = url.startsWith('/api/') ? url : `/api/v1${url}`;
  const method = options.method ?? 'GET';
  const verbose = isVerbose();
  const t0 = verbose ? performance.now() : 0;

  let response: Response;
  try {
    response = await fetch(`${_config.baseUrl}${resolvedUrl}`, {
      ...options,
      headers,
      credentials: 'include',
    });
  } catch (networkErr) {
    if (verbose) logRequest(method, resolvedUrl, 0, Math.round(performance.now() - t0));
    throw new ApiHttpError(
      0,
      networkErr instanceof Error ? networkErr.message : 'Network error',
      'NETWORK_ERROR',
      undefined,
      resolvedUrl,
      method,
    );
  }

  const { status } = response;

  if (status === 401 && _refreshCallback) {
    if (!_refreshInFlight) {
      _refreshInFlight = _refreshCallback().finally(() => {
        _refreshInFlight = null;
      });
    }
    const newToken = await _refreshInFlight;
    if (newToken) {
      let retryResponse: Response;
      try {
        retryResponse = await fetch(`${_config.baseUrl}${resolvedUrl}`, {
          ...options,
          headers: { ...headers, Authorization: `Bearer ${newToken}` },
          credentials: 'include',
        });
      } catch (networkErr) {
        if (verbose) logRequest(method, resolvedUrl, 0, Math.round(performance.now() - t0));
        throw new ApiHttpError(
          0,
          networkErr instanceof Error ? networkErr.message : 'Network error',
          'NETWORK_ERROR',
          undefined,
          resolvedUrl,
          method,
        );
      }
      if (retryResponse.ok) {
        const retryData = retryResponse.status === 204 ? undefined : await retryResponse.json();
        if (verbose)
          logRequest(method, resolvedUrl, retryResponse.status, Math.round(performance.now() - t0));
        return {
          data: retryData,
          status: retryResponse.status,
          headers: retryResponse.headers,
        } as T;
      }
    }
    if (verbose) logRequest(method, resolvedUrl, 401, Math.round(performance.now() - t0));
    throw new ApiUnauthorizedError();
  }

  if (verbose) logRequest(method, resolvedUrl, status, Math.round(performance.now() - t0));

  if (status === 401 || status === 403) throw new ApiUnauthorizedError();

  if (!response.ok) {
    const traceId = response.headers.get('X-Request-Id') ?? undefined;
    let message: string | undefined;
    let code: string | undefined;
    try {
      const body = (await response.json()) as {
        message?: string;
        code?: string;
        detail?: string;
        title?: string;
      };
      // admin-bff uses { code, message }; Go services use RFC 9457 { title, detail }
      message =
        (typeof body.detail === 'string' ? body.detail : undefined) ??
        (typeof body.message === 'string' ? body.message : undefined) ??
        (typeof body.title === 'string' ? body.title : undefined);
      code = typeof body.code === 'string' ? body.code : undefined;
    } catch {
      // ignore parse errors
    }
    throw new ApiHttpError(status, message, code, traceId, resolvedUrl, method);
  }

  const data = status === 204 ? undefined : await response.json();
  return { data, status, headers: response.headers } as T;
}

/**
 * Extracts .data from an orval response object.
 * Since errors are thrown by adminBffFetch, .data is always the success payload.
 */
export function unwrapData<TData>(response: { data: TData } | null | undefined): TData | null {
  if (response == null) return null;
  return response.data ?? null;
}
